/* eslint-disable max-lines */
/* global RequestInit */
import { parse } from 'query-string';
import buildUrl from 'build-url';
import { HeaderBarBannerApiResponse } from '../../client/redux/headerBarBanner/types';
import { ValuePropsApiResponse } from '../../client/redux/valuePropositions/types';
import { BoughtProductAPIResponse } from '../../client/redux/user/lastBoughtProduct/types';
import {
    PDPApiResponse,
    PdpRequestQueryParam,
    ColorVariantsAPIResponse,
    ProductSimpleVideoResponse,
} from '../../client/redux/productdetail/types';
import { FabricSampleOrderResponse, ApiFabricSampleOrder } from '../../client/redux/productdetail/fabricSample';
import { HomePageCMSResponse } from '../../client/redux/homepage/types';
import { ListingPageUrlState, ProductsResponse } from '../../client/redux/products/types';
import { CategoryResponse } from '../../client/redux/category/types';
import { DynamicSearchSuggestionsResponse } from '../../client/redux/dynamicSearch/types';
import { SearchRedirectsResponse, SearchSuggestionResponse } from '../../client/redux/search/types';
import { ClubIntegrationPixelResponse, LoginResponse, RegisterUserValues } from '../../client/redux/user/types';
import { CartCountResponse, CartResponse } from '../../client/redux/cart/types';
import { WishlistResponse } from '../../client/redux/wishlist/types';
import { Config } from '../types/config';
import { LandingPageRedirectsResponse, RedirectResponse } from '../../client/redux/redirect/types';
import { SeoResponse } from '../../client/redux/seo/types';
import { isRedirected } from '../../client/redux/redirect/actions';
import { PER_PAGE } from '../paginationService';
import { BellaThunkDispatch } from '../../client/redux/types';
import { HelpCenterArticlesResponse } from '../../client/redux/helpCenter/types';
import { PartialLocationObject } from '../../server/types/bellaRequest';
import { ContentInfusionResponse } from '../../client/redux/infusions/types';
import { LooksResponse } from '../../client/redux/looks/types';
import { BrandsResponse } from '../../client/redux/brands/types';
import { PageType } from '../../client/hooks/UrlManager/types';
import { getStrippedPath, addSlash } from '../helpers/urlHelpers';
import { ProductReviewsResponse, FetchRatingsQueryParams } from '../../client/redux/productReviews/types';
import CustomFetchApiError from '../errors/CustomFetchApiError';
import { composeGetProductsPath, convertFilters, getTypeSpecificQueryParams } from './shopApiUrlService';
import GenericApiAdapter from './genericApiAdapter';
import shopApiRoutes, { CMS_STATIC_BLOCK_KEY_TEMPLATE } from './apiRoutes';
import {
    CmsBlockStaticBlockResponse,
    CmsStaticPageResponse,
    ConfigurationResponse,
    EmailExistsResponse,
    GenericShopApiResponse,
    HeaderBarBannerResponse,
    ResendDoiConfirmationResponse,
    IsFacebookOnlyUserResponse,
    AddToCartParams,
} from './types';
import {
    custom404ErrorHandler,
    defaultErrorHandler,
    ErrorHandlerFunction,
    exemptStatusErrorHandler,
} from './ErrorHandlers';
import { LooksListingResponse, LooksListingFilterParams } from 'Client/redux/looksPage/types';
import { LookDetailResponse } from 'Client/redux/lookDetail/types';
import { LooksSliderResponse } from 'Client/redux/looksSlider/types';

/* eslint-disable-next-line @typescript-eslint/camelcase */
const productsQueryParams = { perPage: PER_PAGE, nomemcached: true, briefData: true, hydrate_filters: true };
type RedirectResponseInterface = GenericShopApiResponse<RedirectResponse>;

export const isRedirectResponse = <T>(
    response: GenericShopApiResponse<T> | RedirectResponseInterface
): response is RedirectResponseInterface => {
    const data = response.data as any;
    return data && !!data.targetPath;
};

interface ParsedQuery {
    [key: string]: string | string[] | null | undefined;
}

export const parseRedirect = ({ currentSearch = '', currentPathname = '', targetPath = '' }) => {
    let finalPath = targetPath;
    let queryParams: ParsedQuery = parse(currentSearch);

    // handle case of empty currentSearch but there being prams in the currentPathName
    if (currentPathname.includes('?')) {
        const [, post] = currentPathname.match(/\?(.*)/) || ['', ''];
        queryParams = {
            ...queryParams,
            ...parse(post),
        };
    }

    /**
     * handle case where target path already has query string
     * break the query string off and add it's parts to queryParams
     */
    if (targetPath.includes('?')) {
        const [, pre, post] = targetPath.match(/^(.*)\?(.*)/) || ['', '', ''];
        queryParams = {
            ...queryParams,
            ...parse(post),
        };
        if (pre) {
            finalPath = pre;
        }
    }

    let searchedKeyword = queryParams.searched;
    if (queryParams.q) {
        searchedKeyword = queryParams.q;
    }
    if (searchedKeyword) {
        queryParams = {
            ...queryParams,
            searched: searchedKeyword,
        };
    }

    delete queryParams.q;

    if (Object.keys(queryParams).length > 0) {
        return addSlash(
            buildUrl(finalPath, {
                queryParams: queryParams as { [name: string]: string },
            })
        );
    }

    return addSlash(targetPath);
};

export default class ShopApi {
    private apiAdapter: GenericApiAdapter;

    private dispatch: BellaThunkDispatch;

    private readonly location: any;

    private readonly previewMode: boolean;

    // eslint-disable-next-line max-params
    public constructor(
        apiBaseUrl: string,
        config: Config,
        country: string,
        userAgent: string,
        location: PartialLocationObject
    ) {
        this.location = location;
        this.previewMode = location.host.includes('preview');
        this.apiAdapter = new GenericApiAdapter(apiBaseUrl, config, userAgent, { country });
    }

    public setDispatch(dispatch: BellaThunkDispatch) {
        this.dispatch = dispatch;
    }

    // eslint-disable-next-line max-params
    private async get<T>(
        url: string,
        queryParams: object = {},
        headers: object = {},
        errorHandler: ErrorHandlerFunction = defaultErrorHandler,
        fullResponse: boolean = false
    ): Promise<T> {
        const newHeaders = {
            ...headers,
            'x-requested-url': this.location ? this.location.pathname : '',
        };

        const extendedQueryParams = this.previewMode ? { ...queryParams, preview: 1 } : queryParams;
        const response = await this.apiAdapter.get(url, extendedQueryParams, newHeaders, errorHandler, fullResponse);
        const genericResponse = response as RedirectResponseInterface;

        /**
         * as mentioned in docs/redirects.md this is where the bob url
         * manager redirects are handled. There is also special logic here to
         * carry over the searched query param.
         */
        if (isRedirectResponse(genericResponse)) {
            if (this.dispatch) {
                this.dispatch(
                    isRedirected({
                        ...genericResponse.data,
                        targetPath: addSlash(
                            parseRedirect({
                                currentSearch: this.location.search,
                                currentPathname: this.location.pathname,
                                targetPath: genericResponse.data.targetPath,
                            })
                        ),
                        source: 'bob url manager redirect',
                    })
                );
            }
            return Promise.reject(new CustomFetchApiError('A Redirect Occurred', undefined, true));
        }

        return response as T;
    }

    // eslint-disable-next-line max-params
    private async post(
        url: string,
        body: RequestInit['body'],
        queryParams: object = {},
        headers: object = {},
        errorHandler: ErrorHandlerFunction = defaultErrorHandler
    ): Promise<Response> {
        const newHeaders = {
            ...headers,
            'Content-Type': 'application/json',
        };
        return this.apiAdapter.post(url, body, queryParams, newHeaders, errorHandler);
    }

    public getCategoryTree(): Promise<CategoryResponse> {
        // eslint-disable-next-line @typescript-eslint/camelcase
        return this.get<CategoryResponse>(shopApiRoutes.CATEGORY_TREE, { includeFlyout: 1, show_hidden: true });
    }

    public getContentInfusion(pageType: PageType, pageKey: string): Promise<ContentInfusionResponse> {
        return this.get<ContentInfusionResponse>(
            `${shopApiRoutes.CONTENT_INFUSION}/${pageType}/${pageKey}`,
            {},
            {},
            custom404ErrorHandler('Could not fetch content infusion sliders')
        );
    }

    public getLooks(): Promise<LooksResponse> {
        return this.get<LooksResponse>(shopApiRoutes.LOOKS);
    }

    public getLooksSlider(params: { slug?: string }): Promise<LooksSliderResponse> {
        return this.get<LooksSliderResponse>(shopApiRoutes.LOOKS_SLIDER, params);
    }

    public getBrands() {
        return this.get<BrandsResponse>(shopApiRoutes.BRANDS);
    }

    public getFeatureToggles(): Promise<GenericShopApiResponse<{}>> {
        return this.get<GenericShopApiResponse<{}>>(shopApiRoutes.FEATURE_TOGGLES);
    }

    public getRemoteConfig(): Promise<ConfigurationResponse> {
        return this.get<ConfigurationResponse>(shopApiRoutes.REMOTE_CONFIG);
    }

    public getHelpCenterArticles(count: number): Promise<HelpCenterArticlesResponse> {
        return this.get<HelpCenterArticlesResponse>(shopApiRoutes.HELP_CENTER, {
            count,
        });
    }

    public getSeoData(urlPath: string, state: ListingPageUrlState): Promise<SeoResponse> {
        return this.get(
            `${shopApiRoutes.SEO}/${urlPath}`,
            {
                ...convertFilters(state.appliedFilters),
            },
            {},
            exemptStatusErrorHandler([404])
        );
    }

    public async loginWithHash(loginHash: string, userCookies?: string): Promise<any> {
        return this.post(
            shopApiRoutes.USER_LOGIN_WITH_HASH,
            JSON.stringify({ loginHash }),
            {},
            {
                cookie: userCookies,
            }
        );
    }

    public async healthCheck(): Promise<boolean> {
        const response = await this.get<GenericShopApiResponse<{}>>(shopApiRoutes.HEALTH_CHECK);
        return response.success;
    }

    public async getTranslations(): Promise<{}> {
        const response = await this.get<
            GenericShopApiResponse<{ version: string; translations: { [key: string]: string } }>
        >(shopApiRoutes.TRANSLATIONS);
        return response.data.translations;
    }

    public async translationsVersion(): Promise<string> {
        const response = await this.get<GenericShopApiResponse<{ version: string }>>(
            shopApiRoutes.TRANSLATIONS_VERSION
        );

        if (response.data && response.data.version) {
            return response.data.version;
        }

        return '';
    }

    public getSearchSuggestions(keyword: string): Promise<SearchSuggestionResponse> {
        return this.get<SearchSuggestionResponse>(shopApiRoutes.SEARCH_SUGGESTION, { keyword });
    }

    public getDynamicSearchSuggestions(keyword: string): Promise<DynamicSearchSuggestionsResponse> {
        return this.get<DynamicSearchSuggestionsResponse>(shopApiRoutes.DYNAMIC_SEARCH_SUGGESTIONS, { keyword });
    }

    public getSearchRedirects(q: string): Promise<SearchRedirectsResponse> {
        return this.get<SearchRedirectsResponse>(shopApiRoutes.SEARCH_REDIRECTS, { q });
    }

    public async loginWithFacebook(token: string): Promise<LoginResponse> {
        const response = await this.post(shopApiRoutes.LOGIN_FACEBOOK, JSON.stringify({ token }));
        return response.json();
    }

    public async getProducts(path: string, options: object) {
        return this.get<ProductsResponse>(path, {
            ...productsQueryParams,
            ...options,
        });
    }

    public async getProductsForListingState(state: ListingPageUrlState) {
        const path = composeGetProductsPath(state);
        const typeSpecificParams = getTypeSpecificQueryParams(state);

        const productsResponse = await this.getProducts(path, {
            ...productsQueryParams,
            ...convertFilters(state.appliedFilters),
            ...typeSpecificParams,
            ...state.sortingParams,
            page: state.page,
        });

        if (!productsResponse.success && !isRedirectResponse(productsResponse)) {
            throw new Error(`Products not found for path: ${path}`);
        }

        return productsResponse;
    }

    public async getProductsForDetailPage(urlKey: string, queryParams: PdpRequestQueryParam): Promise<PDPApiResponse> {
        return this.get(`${shopApiRoutes.PRODUCT_DETAIL_PAGE}/${getStrippedPath(urlKey)}`, queryParams);
    }

    public async getProductColorVariantsBySimpleSku(
        urlKey: string,
        simpleSku: string
    ): Promise<ColorVariantsAPIResponse> {
        return this.get(`${shopApiRoutes.PRODUCT_DETAIL_PAGE}/${getStrippedPath(urlKey)}/variants`, { simpleSku });
    }

    public getProductRelativesForDetailPage(urlKey: string): Promise<ProductsResponse> {
        return this.get<ProductsResponse>(
            `${shopApiRoutes.PRODUCT_DETAIL_PAGE}${urlKey}/related`,
            {},
            {},
            custom404ErrorHandler('Could not fetch product relatives')
        );
    }

    public getProductVideoBySimpleSku(
        urlKey: string,
        queryParams: PdpRequestQueryParam
    ): Promise<ProductSimpleVideoResponse> {
        return this.get<ProductSimpleVideoResponse>(
            `${shopApiRoutes.PRODUCT_DETAIL_PAGE}${urlKey}/video`,
            queryParams,
            {},
            custom404ErrorHandler('Could not fetch product simple video')
        );
    }

    public getProductReviews(sku: string, queryParams?: FetchRatingsQueryParams): Promise<ProductReviewsResponse> {
        return this.get<ProductReviewsResponse>(`${shopApiRoutes.PRODUCT_REVIEWS}/${sku}/reviews`, queryParams);
    }

    public getProductReviewsByUrlkey(
        urlKey: string,
        queryParams?: FetchRatingsQueryParams
    ): Promise<ProductReviewsResponse> {
        return this.get<ProductReviewsResponse>(
            `${shopApiRoutes.PRODUCT_REVIEWS}/url_key${urlKey}/reviews`,
            queryParams
        );
    }

    public async register(userValues: RegisterUserValues): Promise<LoginResponse> {
        const response = await this.post(shopApiRoutes.USER_REGISTER, JSON.stringify(userValues));
        return response.json();
    }

    public async authenticate(username: string, password: string): Promise<LoginResponse> {
        const response = await this.post(shopApiRoutes.USER_LOGIN, JSON.stringify({ username, password }));
        return response.json();
    }

    public isAuthenticated(): Promise<LoginResponse> {
        return this.get(shopApiRoutes.IS_AUTHENTICATED);
    }

    public isAuthenticatedFullResponse(userCookies?: string): Promise<Response> {
        return this.get(
            shopApiRoutes.IS_AUTHENTICATED,
            {},
            {
                cookie: userCookies,
            },
            defaultErrorHandler,
            true
        );
    }

    public userEmailExists(email: string): Promise<EmailExistsResponse> {
        return this.get<EmailExistsResponse>(shopApiRoutes.USER_EMAIL_EXISTS, { email });
    }

    public userIsFacebookOnly(email: string): Promise<IsFacebookOnlyUserResponse> {
        return this.get<IsFacebookOnlyUserResponse>(shopApiRoutes.USER_IS_FACEBOOK_ONLY, { email });
    }

    public getClubIntegrationPixel(): Promise<ClubIntegrationPixelResponse> {
        return this.get(shopApiRoutes.USER_CLUB_INTEGRATION_PIXEL);
    }

    public getCartCount() {
        return this.get<CartCountResponse>(shopApiRoutes.GET_CART_COUNT);
    }

    public getCartData() {
        return this.get<CartResponse>(shopApiRoutes.GET_CART, { fullData: true });
    }

    public addToCart(addToCarQueryParams: AddToCartParams) {
        return this.get<CartResponse>(shopApiRoutes.CART_ADD, addToCarQueryParams);
    }

    public getWishList(): Promise<WishlistResponse> {
        return this.get<WishlistResponse>(shopApiRoutes.WISHLIST, {}, {}, exemptStatusErrorHandler([401]));
    }

    public addToWishList(simpleSku: string): Promise<WishlistResponse> {
        return this.get<WishlistResponse>(
            `${shopApiRoutes.WISHLIST_ADD}`,
            { simplesku: simpleSku },
            {},
            exemptStatusErrorHandler([401])
        );
    }

    public removeFromWishList(simpleSku: string): Promise<WishlistResponse> {
        return this.get<WishlistResponse>(
            `${shopApiRoutes.WISHLIST_REMOVE}`,
            { simplesku: simpleSku },
            {},
            exemptStatusErrorHandler([401])
        );
    }

    public getLandingAndStyleRedirects(): Promise<LandingPageRedirectsResponse> {
        return this.get<LandingPageRedirectsResponse>(shopApiRoutes.LANDING_AND_STYLE_REDIRECTS);
    }

    public getHeaderBarBannerData(): Promise<HeaderBarBannerResponse> {
        return this.get<HeaderBarBannerResponse>(`${shopApiRoutes.HEADER_BAR_BANNER_DATA}`, {
            fullData: true,
        });
    }

    public getCMSStaticBlockLinkBanners(cmsBlockKey: string): Promise<CmsBlockStaticBlockResponse> {
        return this.get<CmsBlockStaticBlockResponse>(
            shopApiRoutes.CMS_STATIC_BLOCK.replace(CMS_STATIC_BLOCK_KEY_TEMPLATE, cmsBlockKey)
        );
    }

    public getCMSStaticPage(pageKey: string): Promise<CmsStaticPageResponse> {
        return this.get<CmsStaticPageResponse>(`${shopApiRoutes.CMS}/${pageKey}/staticpage`);
    }

    public getHomePageCmsData(): Promise<HomePageCMSResponse> {
        return this.get<HomePageCMSResponse>(`${shopApiRoutes.HOMEPAGE_CMS}`);
    }

    public getHomePageNewProducts(perPage: number): Promise<ProductsResponse> {
        return this.get<ProductsResponse>(`${shopApiRoutes.HOMEPAGE_NEW_PRODUCTS}`, { perPage, nomemcached: true });
    }

    public getHomePageBestSellerProducts(count: number): Promise<ProductsResponse> {
        return this.get<ProductsResponse>(`${shopApiRoutes.HOMEPAGE_BEST_SELLER_PRODUCTS}`, {
            count,
            nomemcached: true,
        });
    }

    public getHomepageSeoData(): Promise<SeoResponse> {
        return this.get<SeoResponse>(`${shopApiRoutes.HOMEPAGE_SEO}`, {}, {}, exemptStatusErrorHandler([404]));
    }

    public async resendDOIConfirmationEmail(isNewsletterSubscribed = true): Promise<ResendDoiConfirmationResponse> {
        const response = await this.post(
            shopApiRoutes.CLUB_REGISTRATION_RESEND_DOI_EMAIL,
            JSON.stringify({
                isNewsletterSubscribed,
            })
        );
        return response.json();
    }

    public getLastBoughtProduct(): Promise<BoughtProductAPIResponse> {
        return this.get(shopApiRoutes.LAST_BOUGHT_PRODUCT);
    }

    public getHeaderBarBannerV2(): Promise<HeaderBarBannerApiResponse> {
        return this.get<HeaderBarBannerApiResponse>(shopApiRoutes.HEADER_BAR_BANNER_V2);
    }

    public getValuePropositions(): Promise<ValuePropsApiResponse> {
        return this.get<ValuePropsApiResponse>(shopApiRoutes.VALUE_PROPOSITIONS);
    }

    public setBackInStockReminder(simpleSku: string, email: string) {
        const body = JSON.stringify({ email });
        return this.post(shopApiRoutes.BACK_IN_STOCK_REMINDER.replace('{simpleSku}', simpleSku), body);
    }

    public async orderFabricSample(fabricSampleData: ApiFabricSampleOrder): Promise<FabricSampleOrderResponse> {
        const body = JSON.stringify(fabricSampleData);
        const response = await this.post(shopApiRoutes.FABRIC_SAMPLE, body);
        return response.json();
    }

    public getLooksListings(queryParams: LooksListingFilterParams): Promise<LooksListingResponse> {
        return this.get<LooksListingResponse>(shopApiRoutes.LOOKSPAGE_LISTING, queryParams);
    }

    public getLookDetail(urlKey: string): Promise<LookDetailResponse> {
        return this.get<LookDetailResponse>(`${shopApiRoutes.LOOKSPAGE_DETAIL}/${getStrippedPath(urlKey)}`);
    }
}
